import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useReactFlow, Edge } from 'reactflow';
import { Button } from '@/shared/components/ui/button';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { Play, Pause, StopCircle } from 'lucide-react';
import { WorkflowTest, WorkflowTestCallbacks } from "./WorkflowTest";
import WebhookWaitingDialog from './WebhookWaitingDialog';
import StorageTriggerWaitingDialog from './StorageTriggerWaitingDialog';
import ScheduleWaitingDialog from './ScheduleWaitingDialog';

interface WorkflowTestComponentProps {
  organizationId: string; // ID da organização atual
  workspaceId: string; // ID do produto atual
}

export const WorkflowTestComponent: React.FC<WorkflowTestComponentProps> = ({ 
  organizationId,
  workspaceId
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { getNodes, getEdges, setNodes, setEdges } = useReactFlow();
  
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTrigger, setCurrentTrigger] = useState<{
    type: 'webhook' | 'storage';
    nodeId: string;
    config: Record<string, unknown>;
  } | null>(null);
  
  const [scheduleModal, setScheduleModal] = useState<{
    isOpen: boolean;
    nodeId: string;
    scheduleData: Record<string, unknown>;
  }>({ isOpen: false, nodeId: '', scheduleData: {} });
  
  // Ref para manter referência sempre atualizada do estado do modal
  const scheduleModalRef = useRef(scheduleModal);
  
  const workflowTestRef = useRef<WorkflowTest | null>(null);
  
  // Manter o ref sempre atualizado com o estado atual do modal
  useEffect(() => {
    scheduleModalRef.current = scheduleModal;
  }, [scheduleModal]);
  
  // Inicializa a instância WorkflowTest
  useEffect(() => {
    const callbacks: WorkflowTestCallbacks = {
      onNodeStatusChange: handleNodeStatusChange,
      onComplete: handleTestComplete,
      onTriggerWaiting: handleTriggerWaiting,
      onWebhookReceived: handleWebhookReceived,
      onWebhookProcessed: handleWebhookProcessed,
      onWorkflowStatusChange: handleWorkflowStatusChange,
      onWorkflowCompleted: handleWorkflowCompleted, // Adicionar o novo callback
      onScheduleRegistered: handleScheduleRegistered, // Callback para schedule registrado
      onScheduleTriggered: handleScheduleTriggered, // Callback para schedule executado
    };
    
    workflowTestRef.current = new WorkflowTest(callbacks);
    
    // Limpar ao desmontar
    return () => {
      if (workflowTestRef.current) {
        workflowTestRef.current.stopTest();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Implementar a função de callback para workflow-completed
  const handleWorkflowCompleted = useCallback((data?: Record<string, unknown>) => {
    console.log("Workflow concluído com sucesso:", data);
    
    // Notificar o usuário com um toast personalizado - fundo verde e letras brancas
    toast({
      title: t("workflow.test.completed"),
      description: t("workflow.test.completedDescription"),
      className: "bg-green-600 text-white border-green-700",
    });
    
    // Atualizar o estado local se necessário
    setIsRunning(false);
  }, [toast, t]);

  // Atualiza o status de um nó
  const handleNodeStatusChange = (nodeId: string, status: string, result?: Record<string, unknown> | null) => {
    console.log(`Atualizando status do nó ${nodeId} para ${status}`, result);
    
    // Log detalhado antes da atualização
    setNodes((prevNodes) => {
      // Encontrar o nó que queremos atualizar para verificar seu estado atual
      const nodeToUpdate = prevNodes.find(n => n.id === nodeId);
      console.log(`Estado atual do nó ${nodeId} antes da atualização:`, 
        nodeToUpdate ? {
          type: nodeToUpdate.data.type,
          currentStatus: nodeToUpdate.data.status,
          webhookStatus: nodeToUpdate.data.webhookStatus
        } : 'Nó não encontrado');
      
      // Aplicar a atualização
      const updatedNodes = prevNodes.map((node) => {
        if (node.id === nodeId) {
          // Verificar se é um nó de trigger ou webhook
          const isTriggerNode = 
            node.data.type?.includes('trigger') || 
            node.data.type?.includes('webhook') || 
            // Verificar também campos de nomes para detectar triggers
            node.data.name?.includes('trigger') || 
            node.data.name?.includes('webhook');
          
          // Para nós de webhook/trigger que estão em 'waiting' ou que foram completados ('success'),
          // garantir que o status seja atualizado corretamente
          // Verificação especial para garantir que nós de trigger que completam mudem para 'success'
          const finalStatus = 
            // Se o nó já está em 'waiting' e recebe um status 'running' após um evento
            // mas já foi marcado como completed em algum lugar, força para 'success'
            (isTriggerNode && 
             node.data.status === 'waiting' && 
             status === 'running' && 
             node.data.webhookProcessed) ? 'success' : status;
          
          // Verificar se o webhook foi processado e forçar o status 'success'
          const webhookProcessed = 
            // Se o status enviado é 'success', ou se houver resultado de webhook processado
            (status === 'success') || 
            (isTriggerNode && status === 'running' && result?.webhookProcessed) ||
            (node.data.webhookProcessed);
          
          const updatedNode = {
            ...node,
            data: {
              ...node.data,
              status: finalStatus,
              loading: finalStatus === 'running',
              testOutput: result,
              // Para nós de trigger, guardar informação adicional
              ...(isTriggerNode ? { 
                webhookStatus: finalStatus, 
                webhookProcessed: webhookProcessed 
              } : {})
            }
          };
          
          console.log(`Nó ${nodeId} após atualização:`, {
            type: updatedNode.data.type,
            newStatus: updatedNode.data.status,
            webhookStatus: updatedNode.data.webhookStatus,
            webhookProcessed: updatedNode.data.webhookProcessed
          });
          
          return updatedNode;
        }
        return node;
      });
      
      return updatedNodes;
    });
    
    // Quando um nó é marcado como "success", propagamos o status para as arestas que saem dele
    if (status === 'success') {
      // Usando getEdges para obter as arestas atuais
      const currentEdges = getEdges();
      // Filtrando apenas as arestas que saem do nó que foi completado
      const edgesToUpdate = currentEdges.filter(edge => edge.source === nodeId);
      
      console.log(`Encontradas ${edgesToUpdate.length} arestas saindo do nó ${nodeId} para atualizar`);
      
      if (edgesToUpdate.length > 0) {
        setEdges(prevEdges => {
          return prevEdges.map(edge => {
            if (edge.source === nodeId) {
              console.log(`Marcando aresta ${edge.id} como sucesso porque o nó ${nodeId} foi completado com sucesso`);
              return {
                ...edge,
                // Definir explicitamente a propriedade success como true para a aresta
                data: {
                  ...(edge.data || {}),
                  success: true
                }
              };
            }
            return edge;
          });
        });
      }
    }
  };
  
  // Trata a conclusão do teste
  const handleTestComplete = (status: string, results?: Record<string, unknown>) => {
    console.log('Teste concluído com status:', status, results);
    setIsRunning(false);
    setIsPaused(false);
    setCurrentTrigger(null);
    
    toast({
      title: status === 'success' 
        ? t('workflow.test.success_title') 
        : t('workflow.test.error_title'),
      description: status === 'success'
        ? t('workflow.test.success_description')
        : t('workflow.test.error_description', { error: results?.error || '' }),
      variant: status === 'success' ? 'default' : 'destructive'
    });
  };
  
  // Trata a espera por trigger
  const handleTriggerWaiting = (triggerType: string, nodeId: string, config: Record<string, unknown>) => {
    console.log(`Node ${nodeId} está esperando por trigger do tipo ${triggerType}`, config);
    
    // Para garantir que temos uma URL de webhook válida
    let webhookUrl: unknown;
    if (triggerType === 'webhook') {
      webhookUrl = config.webhookUrl || config.webhookPath || config.path;
      console.log(`URL do webhook encontrada: ${webhookUrl}`);

      // Armazenar o webhookId para comparação posterior
      if (config.id || config.webhookId) {
        const webhookId = config.id || config.webhookId;
        console.log(`🔑 WebhookId capturado: ${webhookId} para o nodeId: ${nodeId}`);

        // Garantir que armazenamos o webhookId no config para usá-lo depois
        config.webhookId = webhookId;
      }

      // Registrar o webhook ativo para facilitar o fechamento do diálogo
      if (workflowTestRef.current && typeof webhookUrl === 'string') {
        workflowTestRef.current.registerActiveWebhook(nodeId, webhookUrl);
      }
    }
    
    setCurrentTrigger({
      type: triggerType as 'webhook' | 'storage',
      nodeId,
      config: {
        ...config,
        webhookUrl // Garantir que a URL está sempre disponível
      }
    });
  };

  // Trata evento de webhook recebido
  const handleWebhookReceived = (nodeId: string, data: Record<string, unknown>) => {
    console.log(`📣 Webhook recebido para o nó ${nodeId}:`, data);
    
    // Extrair informações importantes do evento
    const webhookId = data.id;
    // Obter o ID do webhook atual a partir da configuração do trigger
    const currentWebhookId = currentTrigger?.type === 'webhook' ? 
      (currentTrigger.config?.webhookId || currentTrigger.config?.id) : null;
    
    const isTriggeredEvent = data.type === 'webhook-triggered' || data.event === 'webhook-triggered';
    
    // Verificar se há um diálogo aberto (DOM check mais robusto)
    const webhookDialogElement = document.querySelector('[data-webhook-waiting-node]');
    const webhookDialogOpen = !!webhookDialogElement || currentTrigger?.type === 'webhook';
    const webhookDialogNodeId = webhookDialogElement?.getAttribute('data-webhook-waiting-node');
    
    // Adicionar logs detalhados para diagnóstico
    console.log(`📣 DEBUG: isTriggeredEvent=${isTriggeredEvent}, webhookDialogOpen=${webhookDialogOpen}`);
    console.log(`📣 DEBUG: webhookDialogNodeId=${webhookDialogNodeId}, nodeId=${nodeId}`);
    console.log(`📣 DEBUG: currentTrigger=`, currentTrigger);
    console.log(`📣 DEBUG: currentWebhookId=${currentWebhookId}, webhookId=${webhookId}`);
    
    // Novas verificações:
    // 1. Se o nodeId do evento corresponde ao nodeId do diálogo aberto
    // 2. Se o ID do webhook corresponde ao ID registrado
    const isNodeMatch = webhookDialogNodeId === nodeId;
    const isIdMatch = webhookId && currentWebhookId && webhookId === currentWebhookId;
    
    console.log(`📣 DEBUG: isNodeMatch=${isNodeMatch}, isIdMatch=${isIdMatch}`);
    
    // Estratégia 1: Se temos qualquer tipo de evento de webhook e há um diálogo aberto
    if (webhookDialogOpen) {
      console.log(`📣 🚨 DETECTADO DIÁLOGO DE WEBHOOK ABERTO`);
      
      // Fechar o diálogo se:
      // - É um evento triggered, OU
      // - O nodeId corresponde, OU
      // - Os IDs de webhook correspondem
      if (isTriggeredEvent || isNodeMatch || isIdMatch) {
        console.log(`📣 🚨 FECHANDO DIÁLOGO DE WEBHOOK IMEDIATAMENTE - Condição ativa: isTriggered=${isTriggeredEvent}, isNodeMatch=${isNodeMatch}, isIdMatch=${isIdMatch}`);
        
        // Força o fechamento do diálogo imediatamente
        setCurrentTrigger(null);
        
        // Log adicional para confirmar que o diálogo foi fechado
        setTimeout(() => {
          const dialogStillOpen = !!document.querySelector('[data-webhook-waiting-node]');
          console.log(`📣 ✅ Verificação pós-fechamento: diálogo ainda aberto=${dialogStillOpen}`);
        }, 50);
        
        // Exibir toast após fechar o diálogo
        toast({
          title: t('workflow.test.webhook_received_title'),
          description: t('workflow.test.webhook_received_description'),
        });
        
        return;
      }
    }
    
    // Para casos não-triggered ou quando não há diálogo aberto
    console.log(`📣 Webhook recebido, mas não é triggered ou não há diálogo aberto`);
    
    // Exibir toast de qualquer forma
    toast({
      title: t('workflow.test.webhook_received_title'),
      description: t('workflow.test.webhook_received_description'),
    });
    
    // Fechar diálogo se estiver aberto (redundância para garantir)
    if (currentTrigger?.type === 'webhook') {
      console.log(`📣 Fechando diálogo de webhook (modo redundante)`);
      setCurrentTrigger(null);
    }
  };

  // Trata evento de webhook processado
  const handleWebhookProcessed = (nodeId: string, data: Record<string, unknown>) => {
    console.log(`📣 Webhook processado para o nó ${nodeId}:`, data);
    
    // Extrair o ID do webhook do evento processado
    const webhookId = data.id;
    console.log(`📣 WebhookId processado: ${webhookId}`);
    
    // Atualizar o status do nó para success explicitamente
    // Isto é crucial - aqui garantimos que o nó de trigger receba o status success
    // após ser processado
    handleNodeStatusChange(nodeId, 'success', {
      ...data,
      webhookProcessed: true // Marcador especial para indicar que o webhook foi processado
    });
    
    // Garantir que o diálogo seja fechado se ainda estiver aberto
    if (currentTrigger?.type === 'webhook') {
      const currentWebhookId = currentTrigger.config?.webhookId;
      console.log(`📣 Verificando IDs para processamento: webhookId atual=${currentWebhookId}, webhookId processado=${webhookId}`);
      
      // Verificar correspondência por webhookId ou nodeId
      if ((webhookId && currentWebhookId && webhookId === currentWebhookId) || 
          currentTrigger.nodeId === nodeId) {
        console.log(`📣 Correspondência encontrada. Fechando diálogo de webhook após processamento.`);
        
        toast({
          title: t('workflow.test.webhook_processed_title', { defaultValue: 'Webhook processado' }),
          description: t('workflow.test.webhook_processed_description', { defaultValue: 'O webhook foi processado com sucesso' }),
        });
        
        setCurrentTrigger(null);
      } else {
        console.log(`📣 Trigger atual não corresponde ao webhook processado`);
      }
    }
  };

  // Trata evento de schedule registrado (abrir modal)
  const handleScheduleRegistered = useCallback((nodeId: string, data: Record<string, unknown>) => {
    // Abrir modal de schedule
    setScheduleModal({
      isOpen: true,
      nodeId: nodeId,
      scheduleData: {
        cronExpression: data.cron,        // Backend envia 'cron', não 'cronExpression'
        scheduledTime: data.nextExecution, // Backend envia 'nextExecution', não 'scheduledTime'
        timezone: data.timezone,
        triggerId: data.id,              // Backend envia 'id', não 'triggerId'
        message: data.message
      }
    });
  }, []);

  // Trata evento de schedule executado (fechar modal)
  const handleScheduleTriggered = useCallback((nodeId: string) => {
    // CORREÇÃO: Usar a referência atual do modal para evitar stale closure
    const currentModal = scheduleModalRef.current;
    
    // Fechar modal se estiver aberto para este nó
    if (currentModal.isOpen && currentModal.nodeId === nodeId) {
      setScheduleModal({ isOpen: false, nodeId: '', scheduleData: {} });
      
      // Exibir toast de sucesso
      toast({
        title: t("workflow.schedule.executed"),
        description: t("workflow.schedule.executedDescription"),
        className: "bg-green-600 text-white border-green-700",
      });
    }
  }, [toast, t]);

  // Inicia o teste do workflow
  const startTest = async () => {
    try {
      if (!workflowTestRef.current) return;
      
      setIsRunning(true);
      setIsPaused(false);
      setCurrentTrigger(null);
      
      // Limpa o status anterior de todos os nós
      setNodes((nodes) =>
        nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            status: undefined,
            testOutput: undefined,
            loading: false
          }
        }))
      );

      // Limpa o status anterior de todas as arestas/edges
      setEdges((edges) =>
        edges.map((edge) => ({
          ...edge,
          data: {
            ...(edge.data || {}),
            success: undefined
          }
        }))
      );
            
      // Inicia o teste com o schema atual
      const nodes = getNodes();
      
      // Obter as edges e adicionar os metadados necessários
      const edges = getEdges().map(edge => {
        // Criar uma cópia da edge com tipagem segura
        const edgeCopy: Edge & { metadata: Record<string, unknown> } = { ...edge, metadata: {} };

        // Para nós de loop (loop/exit), manter o formato atual
        if (edge.sourceHandle === 'loop' || edge.sourceHandle === 'exit') {
          edgeCopy.metadata.loopDirection = edge.sourceHandle;
          edgeCopy.metadata.sourceHandle = edge.sourceHandle;
        }
        // Para nós de condição (true/false), simplificar para ter apenas o campo condition
        else if (edge.sourceHandle === 'true' || edge.sourceHandle === 'false') {
          edgeCopy.metadata = {
            condition: edge.sourceHandle  // Apenas condition com valor "true" ou "false"
          };
        }
        // Verificar se tem dados de condição explícita
        else if (edge.data?.condition) {
          edgeCopy.metadata = {
            condition: edge.data.condition
          };
        }

        // Verificar dados do nó de origem (apenas para edges que não são de condição)
        if (edge.data?.sourceNodeId &&
            edge.sourceHandle !== 'true' &&
            edge.sourceHandle !== 'false') {
          edgeCopy.metadata.sourceNodeId = edge.data.sourceNodeId;
        }

        // Log para debug de metadata
        console.log('Edge preparada para teste:', {
          id: edgeCopy.id,
          source: edgeCopy.source,
          target: edgeCopy.target,
          metadata: edgeCopy.metadata
        });

        return edgeCopy;
      });
      
      await workflowTestRef.current.startTest(nodes, edges, organizationId, workspaceId);
    } catch (error) {
      console.error('Erro ao iniciar teste:', error);
      setIsRunning(false);
      
      toast({
        title: t('workflow.test.error_title'),
        description: t('workflow.test.start_error', { 
          error: error instanceof Error ? error.message : String(error)
        }),
        variant: 'destructive'
      });
    }
  };
  
  // Parar o teste em execução
  const handleStop = async () => {
    if (!workflowTestRef.current) return;
    
    setIsRunning(false);
    setIsPaused(false);
    setCurrentTrigger(null);
    
    try {
      const success = await workflowTestRef.current.stopTest();
      if (success) {
        toast({
          description: t('workflow.test.stopped'),
          duration: 3000
        });
      } else {
        toast({
          variant: 'destructive',
          description: t('workflow.test.stopped_error'),
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Erro ao parar o teste:', error);
      toast({
        variant: 'destructive',
        description: t('workflow.test.stopped_error'),
        duration: 3000
      });
    }
  };

  // Pausar o teste em execução
  const handlePause = async () => {
    if (!workflowTestRef.current || !isRunning) return;
    
    setIsPaused(true);
    
    try {
      const success = await workflowTestRef.current.pauseTest();
      if (success) {
        toast({
          description: t('workflow.test.paused'),
          duration: 3000
        });
      } else {
        setIsPaused(false); // Reverte o estado se falhar
        toast({
          variant: 'destructive',
          description: t('workflow.test.paused_error'),
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Erro ao pausar o teste:', error);
      setIsPaused(false); // Reverte o estado se falhar
      toast({
        variant: 'destructive',
        description: t('workflow.test.paused_error'),
        duration: 3000
      });
    }
  };

  // Continuar um teste pausado
  const handleResume = async () => {
    if (!workflowTestRef.current || !isPaused) return;
    
    setIsPaused(false);
    
    try {
      const success = await workflowTestRef.current.resumeTest();
      if (success) {
        toast({
          description: t('workflow.test.resumed'),
          duration: 3000
        });
      } else {
        setIsPaused(true); // Reverte o estado se falhar
        toast({
          variant: 'destructive',
          description: t('workflow.test.resumed_error'),
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Erro ao retomar o teste:', error);
      setIsPaused(true); // Reverte o estado se falhar
      toast({
        variant: 'destructive',
        description: t('workflow.test.resumed_error'),
        duration: 3000
      });
    }
  };
  
  // Trata os dados recebidos do webhook
  const handleWebhookData = (data: Record<string, unknown>) => {
    console.log('Dados do webhook recebidos no componente:', data);
    if (!currentTrigger) return;
    
    // O backend continuará o processamento automaticamente
    // Não fechamos o diálogo imediatamente para esperar o evento confirmatório
    // do servidor que virá pelo SSE
  };
  
  // Trata os dados recebidos do storage
  const handleStorageData = (data: Record<string, unknown>) => {
    console.log('Dados do storage recebidos:', data);
    if (!currentTrigger) return;
    
    // O backend continuará o processamento automaticamente
    setCurrentTrigger(null);
  };
  
  // Trata o timeout do trigger
  const handleTriggerTimeout = (nodeId: string) => {
    console.log(`Timeout para o nó ${nodeId}`);
    setCurrentTrigger(null);
    
    // Atualiza o nó com status de erro
    handleNodeStatusChange(nodeId, 'error', { error: 'Timeout' });
    
    toast({
      title: t('workflow.test.trigger_timeout_title'),
      description: t('workflow.test.trigger_timeout_description'),
      variant: 'destructive'
    });
  };

  // Trata o timeout do webhook
  const handleWebhookTimeout = (nodeId: string) => {
    console.log(`Timeout do webhook para o node ${nodeId}`);
    
    // Atualiza o status do nó para erro
    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, status: 'error', isLoading: false } }
          : node
      )
    );
    
    toast({
      variant: 'destructive',
      description: t('workflow.test.webhook_timeout'),
      duration: 5000,
    });
    
    // Limpa o trigger atual
    setCurrentTrigger(null);
  };

  // Trata alteração de status do workflow
  const handleWorkflowStatusChange = (
    status: 'running' | 'stopped' | 'paused',
    message?: string
  ) => {
    console.log(`Status do workflow alterado para: ${status}${message ? `, mensagem: ${message}` : ''}`);
    
    // Atualizar o estado da interface com base no status
    switch (status) {
      case 'paused':
        setIsPaused(true);
        toast({
          description: message || t('workflow.test.paused'),
          duration: 3000
        });
        break;
        
      case 'running':
        setIsPaused(false);
        break;
        
      case 'stopped':
        setIsRunning(false);
        setIsPaused(false);
        setCurrentTrigger(null);
        toast({
          description: message || t('workflow.test.stopped'),
          duration: 3000
        });
        break;
    }
  };
  
  return (
    <div className="flex items-center gap-2">
      {!isRunning ? (
        <Button 
          onClick={startTest} 
          variant="outline" 
          size="sm"
          className="gap-1"
        >
          <Play className="w-4 h-4" />
          {t('workflow.test.start')}
        </Button>
      ) : (
        <>
          {!isPaused ? (
            <Button
              onClick={handlePause}
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={!isRunning}
            >
              <Pause className="w-4 h-4" />
              {t('workflow.test.pause')}
            </Button>
          ) : (
            <Button
              onClick={handleResume}
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={!isPaused}
            >
              <Play className="w-4 h-4" />
              {t('workflow.test.resume')}
            </Button>
          )}
          
          <Button
            onClick={handleStop}
            variant="outline"
            size="sm"
            className="gap-1"
          >
            <StopCircle className="w-4 h-4" />
            {t('workflow.test.stop')}
          </Button>
        </>
      )}
      
      {/* Componente de diálogo aguardando webhook */}
      {currentTrigger && currentTrigger.type === 'webhook' && (
        <WebhookWaitingDialog
          isOpen={!!currentTrigger}
          onClose={() => setCurrentTrigger(null)}
          nodeId={currentTrigger.nodeId}
          webhookUrl={currentTrigger.config?.webhookUrl as string}
          fullUrl={currentTrigger.config?.fullUrl as string | undefined}
          onWebhookData={handleWebhookData}
          onWebhookTimeout={handleWebhookTimeout}
          onStopWorkflow={handleStop}
        />
      )}
      
      {/* Diálogo de espera por storage */}
      {currentTrigger?.type === 'storage' && (
        <StorageTriggerWaitingDialog
          isOpen={currentTrigger.type === 'storage'}
          onClose={() => setCurrentTrigger(null)}
          nodeId={currentTrigger.nodeId}
          workspaceId={currentTrigger.config.workspaceId as string}
          organizationId={currentTrigger.config.organizationId as string}
          onStorageData={handleStorageData}
          onTimeout={() => handleTriggerTimeout(currentTrigger.nodeId)}
          fileTypes={currentTrigger.config.fileTypes as string[] | undefined}
        />
      )}
      
      {/* Diálogo de schedule trigger */}
      <ScheduleWaitingDialog
        isOpen={scheduleModal.isOpen}
        onClose={() => setScheduleModal({ isOpen: false, nodeId: '', scheduleData: {} })}
        nodeId={scheduleModal.nodeId}
        scheduleData={scheduleModal.scheduleData}
        onStopWorkflow={handleStop}
      />
    </div>
  );
};
